import {
  DealAvailabilityResponse,
  DealRegionExceptionList,
  DealRegionResponse,
  RegionGeoJson,
  RegionId,
  RegionPayload,
  RegionRecheckState,
  RegionResponse,
  RegionStatisticsResponse,
} from '@business/entities/region';
import { Endpoint } from '../endpoint';

export function fetchRegions() {
  return Endpoint.get<RegionResponse[]>(`@gc/regions`);
}

export function createRegion(data: RegionPayload) {
  return Endpoint.post<RegionResponse>(`@gc/regions`, { data });
}

export function updateRegion(id: RegionId, data: RegionPayload) {
  return Endpoint.patch<RegionResponse>(`@gc/regions/${id}`, { data });
}

/** Boundary only: name and representatives stay as they are. */
export function updateRegionBoundary(id: RegionId, geoJson: RegionGeoJson | null) {
  return Endpoint.patch<RegionResponse>(`@gc/regions/geo-json/${id}`, { data: { geoJson } });
}

export function deleteRegion(id: RegionId) {
  return Endpoint.delete<void>(`@gc/regions/${id}`);
}

export function fetchMapConfig() {
  return Endpoint.get<{ apiKey: string | null }>(`@gc/regions/map-config`);
}

/** Re-check a deal's address against the current territory boundaries. */
export function matchDealRegion(dealId: string) {
  return Endpoint.post<DealRegionResponse>(`@gc/regions/match-deal/${dealId}`);
}

/** Set a deal's territory by hand; null records "no territory". */
export function setDealRegion(dealId: string, regionId: RegionId | null) {
  return Endpoint.patch<DealRegionResponse>(`@gc/regions/deal-region/${dealId}`, { data: { regionId } });
}

/** Put the selected representative on the deal's booked appointment. */
export function assignDealRepresentative(dealId: string, userId: string) {
  return Endpoint.post<DealRegionResponse>(`@gc/regions/assign-representative/${dealId}`, { data: { userId } });
}

/** Coverage, representatives and deal counts by territory and representative. */
export function fetchRegionStatistics() {
  return Endpoint.get<RegionStatisticsResponse>(`@gc/regions/statistics`);
}

/** Check every open deal against today's boundaries in the background; hand-picked territories are kept. */
export function recheckOpenDeals() {
  return Endpoint.post<RegionRecheckState>(`@gc/regions/recheck-deals`);
}

export function fetchRecheckStatus() {
  return Endpoint.get<{ recheck: RegionRecheckState | null }>(`@gc/regions/recheck-status`);
}

export function fetchDealRegionExceptions() {
  return Endpoint.get<DealRegionExceptionList>(`@gc/regions/deal-exceptions`);
}

/** Availability of the deal territory's reps for a slot (unix seconds), else for the deal's next appointment. */
export function fetchDealAvailability(dealId: string, slot?: { start: number; end: number }) {
  return Endpoint.get<DealAvailabilityResponse>(`@gc/regions/deal-availability/${dealId}`, {
    params: slot ? { start: slot.start, end: slot.end } : {},
  });
}
